/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { LogLevel } from '@azure/msal-browser';
const location = window.location.origin;
export const refreshTokenExpirationOffsetSeconds = 12 * 60 * 60; // access token expiration 12 hours
export const redirectUri = `${location}/login`;
export const logoutRedirectUri = `${location}/login`;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const getMsalConfig = (cfg) => {
  const TENANT_NAME = cfg.VITE_AZURE_B2C_TENANT_NAME;
  const CLIENT_ID = cfg.VITE_AZURE_B2C_CLIENT_ID;
  const SIGN_IN_SIGN_UP_FLOW =
    cfg.VITE_AZURE_B2C_SIGN_IN_SIGN_UP_FLOW;

  const msalConfig = {
    auth: {
      clientId: CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
      authority: `https://${TENANT_NAME}.b2clogin.com/${TENANT_NAME}.onmicrosoft.com/${SIGN_IN_SIGN_UP_FLOW}`, // Choose SUSI as your default authority.
      redirectUri: redirectUri,
      knownAuthorities: [`${TENANT_NAME}.b2clogin.com`], // Mark your B2C tenant's domain as trusted.
      //postLogoutRedirectUri: b2cPolicies.redirectLogoutUri, // Indicates the page to navigate after logout.
      navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
      cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
      storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  };
  return msalConfig;
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  pricingPlannerApi: {
    scopes: ['openid'],
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */

export const signUpSignInRequest = (cfg) => {
  const TENANT_NAME = cfg.VITE_AZURE_B2C_TENANT_NAME;
  const SIGN_IN_SIGN_UP_FLOW =
    cfg.VITE_AZURE_B2C_SIGN_IN_SIGN_UP_FLOW;

  return {
    authority: `https://${TENANT_NAME}.b2clogin.com/${TENANT_NAME}.onmicrosoft.com/${SIGN_IN_SIGN_UP_FLOW}`,
    scopes: ['openid'],
    redirectUri: redirectUri,
  };
};
